import React, { useCallback, useState } from 'react';
import { map, path, pick, prop } from 'ramda';
import { createSelector } from 'reselect';
import { LocalPopup } from 'js/components/common/Popup';
import { Range } from 'js/components/common/Range';
import { Button } from 'js/components/common/Button';
import { Icon } from 'js/components/common/Icon';
import { useFormatter } from 'js/hooks/useFormatter';
import { I18n } from 'js/components/common/I18n';
import { usePortletSelector, usePortletTranslations } from 'js/components/system/Portlet';
import { useShowNotification } from 'js/components/common/Notifications';
import { useUserSelector } from 'js/store/hooks';

import { convertComppoints } from '../actions';
import * as hooks from '../hooks';

import style from './index.scss';

export const currencies = {
    freespins: 'FS',
    goldenchips: 'GC'
};

export const popupClassNames = {
    container: style.popupContainer,
    inner: style.popupInner,
    close: style.popupClose,
    header: style.popupHeader
};

export const getConvertMax = ({ convert, conversionRate }) =>
    Math.floor(Number(convert) / conversionRate) * conversionRate;

export const getConvertMin = ({ conversionRate, minimumConversion }) => {
    let convertMin = conversionRate;
    while (convertMin < minimumConversion) {
        convertMin += conversionRate;
    }

    return convertMin;
};

export const getTranslations = ({ t, bonusPlayType, convertMax, convertMin }) => ({
    ptsText: t('pts'),
    convertToText: t('convertTo'),
    conversionTargetText: t(bonusPlayType || 'cash'),
    conversionRateText: t('convertionRate'),
    minimumConversionText: t('minimumConvertion'),
    promptText: t(convertMax > convertMin ? 'conversionPromptText' : 'conversionSinglePromptText'),
    buttonCancelText: t('buttonCancel'),
    buttonConvertText: t('buttonConvert')
});

export const selectMoneyConversionDetails = path(['conversion', 'moneyConversionDetails']);
export const selectBonusConversionDetails = path(['conversion', 'bonusConversionDetails']);
export const selectConversionDetails = ruleId => createSelector(
    selectMoneyConversionDetails,
    selectBonusConversionDetails,
    (money, bonus) =>
        money && money.conversionRuleId === ruleId
            ? money
            : bonus.find(({ conversionRuleId }) => conversionRuleId === ruleId) || {}
);

export default ({ conversionRuleId, toggleConvertPopup }) => {
    const formatter = useFormatter();
    const t = usePortletTranslations();
    const params = hooks.useParams();
    const currency = useUserSelector(prop('currencyCode'));
    const { balances: { convert }, fetchPortlet } = usePortletSelector(pick(['balances', 'fetchPortlet']));
    const conversionDetails = usePortletSelector(selectConversionDetails(conversionRuleId));
    const { bonusPlayType } = conversionDetails;
    const { conversionRate, minimumConversion } = map(Number, conversionDetails);
    const [pointsAmount, setPointsAmount] = useState(getConvertMax({ convert, conversionRate }));
    const currencyCode = currencies[bonusPlayType] || currency;
    const convertMin = getConvertMin({ minimumConversion, conversionRate });
    const convertMax = getConvertMax({ convert, conversionRate });
    const {
        ptsText,
        convertToText,
        conversionTargetText,
        conversionRateText,
        minimumConversionText,
        promptText,
        buttonCancelText,
        buttonConvertText
    } = getTranslations({ t, bonusPlayType, convertMax, convertMin });
    const showNotification = useShowNotification({ iconType: 'loyaltyPoints' });
    const onConvertClick = useCallback(async () => {
        try {
            const { data: { convertedLoyaltyPoints } } = await convertComppoints({
                pointsAmount,
                conversionRuleId
            });

            // re-fetch data after convert
            await fetchPortlet(params, { showLoader: false });

            showNotification({
                message: <I18n
                    t={t}
                    tKey="notification.success"
                    replacers={[`${convertedLoyaltyPoints} ${ptsText}`]}
                />
            });
            toggleConvertPopup();
        } catch (err) {
            console.error(err);
            showNotification({
                message: <I18n t={t} tKey="notification.error" />
            });
            toggleConvertPopup();
        }
    }, [
        params,
        pointsAmount,
        conversionRuleId,
        showNotification,
        ptsText,
        toggleConvertPopup,
        fetchPortlet,
        t
    ]);

    return (
        <LocalPopup
            className={style.popup}
            onClose={toggleConvertPopup}
            classNames={popupClassNames}
        >
            <div className={style.title}>{`${convertToText} ${conversionTargetText}`}</div>
            <p className={style.conversionInfo}>
                {conversionRateText}
                <span className={style.accentedText}>
                    {` ${conversionRate} ${ptsText} = ${formatter.formatCurrency(1, { currencyCode, precision: 0 })}`}
                </span>
                <br />
                {minimumConversionText}
                <span className={style.accentedText}>{` ${minimumConversion} ${ptsText}`}</span>
                <br />
                <span className={style.promptText}>{promptText}</span>
            </p>
            {convertMax > convertMin
                ? <Range
                    className={style.range}

                    labelBefore={true}
                    labelAfter={true}
                    withBars={true}

                    min={convertMin}
                    max={convertMax}
                    step={conversionRate}
                    defaultValue={convertMax}

                    formatBefore={x => `${x} ${ptsText}`}
                    formatAfter={x => formatter.formatCurrency(x / conversionRate, { currencyCode, precision: 0 })}
                    marksBefore={[convertMin, convertMax]}
                    marksAfter={[convertMin, convertMax]}

                    onAfterChange={setPointsAmount}
                />
                : (
                    <div>
                        <div className={style.singleLabel}>{`${convertMin} ${ptsText}`}</div>
                        <Icon type="convertArrow" className={style.singleIcon} />
                        <div className={style.singleLabel}>
                            {formatter.formatCurrency(convertMin / conversionRate, { currencyCode, precision: 0 })}
                        </div>
                    </div>
                )
            }
            <div className={style.buttons}>
                <Button view="secondary"
                    color="negative"
                    size="medium"
                    onClick={toggleConvertPopup}>{buttonCancelText}
                </Button>
                <Button size="medium"
                    onClick={onConvertClick}>{buttonConvertText}</Button>
            </div>
        </LocalPopup>
    );
};
